import { ROUTES } from "../constants";
import { getWorkItemsListPathForPermissions } from "../Pages/Issues/issuesTabPermissions";
import { buildPermissionsFromApiCodes } from "./buildPermissionsFromApi";
import { WORK_ITEMS_SIDEBAR_ANY_KEYS } from "./permissionKeys";
import { APP_ROLE } from "./roleKinds";
import { normalizeRoleName, resolveRoleKind } from "./resolvePermissions";

/** Lowercase label from `me.role.name`, else `me.jobTitle`. */
function normalizedProfileRoleLabel(me) {
  const fromRole = normalizeRoleName(me?.role);
  if (fromRole) return fromRole;
  return String(me?.jobTitle ?? "")
    .trim()
    .toLowerCase();
}

function isDeveloperLabel(label) {
  if (!label) return false;
  if (label === "dev") return true;
  return label.includes("developer");
}

function isDeveloperRole(me) {
  const role = me?.role;
  if (resolveRoleKind(role) === APP_ROLE.DEVELOPER) return true;
  return isDeveloperLabel(normalizedProfileRoleLabel(me));
}

function isLeadLabel(label) {
  if (!label || isDeveloperLabel(label)) return false;

  const exact = new Set([
    "lead",
    "leads",
    "team lead",
    "team leader",
    "project lead",
    "project leader",
    "technical lead",
    "tech lead",
    "qa lead",
    "engineering lead",
    "product lead",
    "delivery lead",
    "scrum lead",
  ]);
  if (exact.has(label)) return true;
  if (label.endsWith(" lead") || label.endsWith(" leads")) return true;
  // Whole word "lead" or "leads" (e.g. "Engineering Leads", "team-lead") but not "Lead Developer"
  if (/\bleads?\b/.test(label) && !/\bdeveloper\b/.test(label)) return true;
  return false;
}

function isAdminOrProjectManagerLabel(label) {
  if (!label) return false;
  if (label === "admin" || label === "administrator") return true;
  return label.includes("project manager");
}

/**
 * Default landing path after authentication (`fetchMe` has populated `me.role`).
 * @param {Record<string, unknown> | null | undefined} me
 * @returns {string}
 */
export function getPostLoginRedirectPath(me) {
  if (me == null || typeof me !== "object") {
    return ROUTES.DASHBOARD;
  }

  const role = me.role;
  const label = normalizedProfileRoleLabel(me);
  const perms = buildPermissionsFromApiCodes(role?.permissions);
  const can = (k) => perms[k] === true;

  if (isDeveloperRole(me)) {
    if (WORK_ITEMS_SIDEBAR_ANY_KEYS.some((k) => can(k))) {
      return getWorkItemsListPathForPermissions(can);
    }
    return ROUTES.DASHBOARD;
  }

  if (isLeadLabel(label)) {
    return ROUTES.PROJECTS;
  }

  if (
    resolveRoleKind(role) === APP_ROLE.ADMIN ||
    resolveRoleKind(role) === APP_ROLE.MANAGER ||
    isAdminOrProjectManagerLabel(label)
  ) {
    return ROUTES.DASHBOARD;
  }

  return ROUTES.DASHBOARD;
}
